import { useDispatch, useSelector } from "react-redux";
import { useCloseOtpAuthorizationPrompt } from "./useCloseOtpAuthorizationPrompt";
import { useGetOrderActionDetails } from "./api/queries";
import { useEffect, useRef, useState } from "react";
import {
  clearAuthUseCaseProp,
  setAuthUseCaseProp,
  setVerifiedOTPCode,
} from "../store/modules/apiErrorHandler";
import { encodeToBase64 } from "../helpers";

export const useOtpAuthTrigger = ({ storeId = null, params = {} }) => {
  const [actionKey, setActionKey] = useState(null);
  const [isOtpCorrect, setIsOtpCorrect] = useState(false);
  const callbackRef = useRef({
    onSuccessCallBack: () => {},
    onErrorCallBack: () => {},
    onSettledCallBack: () => {},
  });

  const dispatch = useDispatch();
  const { verifiedOTPCode, otpAuthorizationError } = useSelector(
    (state) => state.apiErrorReducer,
  );
  const { closeOtpAuthorizationPrompt } = useCloseOtpAuthorizationPrompt();
  const { refetch: fetchOrderActionDetails, isLoading: isLoadingAuthAction } =
    useGetOrderActionDetails({
      store_id: storeId,
      action: actionKey,
      enabled: false,
      params,
    });

  const otpKeyCodes = useSelector((state) => state.apiErrorReducer?.authKeys);

  const handleOrderModuleAuth = ({
    actionKey = null,
    onSuccessCallBack = () => {},
    onErrorCallBack = () => {},
    onSettledCallBack = () => {},
  }) => {
    if (getAuthCodeBaseOnKey(actionKey)) return onSuccessCallBack();

    // Store callbacks
    callbackRef.current = {
      onSuccessCallBack,
      onErrorCallBack,
      onSettledCallBack,
    };

    setActionKey(actionKey);
  };

  const clearLatestOtpAuthCode = (actionKeys = []) => {
    actionKeys?.forEach((actionKey) => {
      dispatch(clearAuthUseCaseProp({ actionKey }));
    });
  };

  const getAuthCodeBaseOnKey = (actionKey) => {
    return otpKeyCodes?.[actionKey]?.code;
  };

  useEffect(() => {
    if (actionKey) {
      fetchOrderActionDetails()
        .then((res) => {
          if (!res?.isSuccess) return callbackRef.current.onErrorCallBack();

          setIsOtpCorrect(true);
        })
        .catch(() => {
          callbackRef.current.onErrorCallBack();
        })
        .finally(() => {
          callbackRef.current.onSettledCallBack();
        });
    }
  }, [actionKey]);

  useEffect(() => {
    if (isOtpCorrect && verifiedOTPCode) {
      const otpCode = verifiedOTPCode;
      dispatch(
        setAuthUseCaseProp({
          code: encodeToBase64(otpCode),
          actionKey,
        }),
      );

      dispatch(setVerifiedOTPCode(null));
      closeOtpAuthorizationPrompt();
      callbackRef.current.onSuccessCallBack();
      callbackRef.current.onSuccessCallBack = () => {};
    }
  }, [isOtpCorrect, verifiedOTPCode]);

  useEffect(() => {
    if (isOtpCorrect && !otpAuthorizationError?.action) {
      setActionKey(null);
      setIsOtpCorrect(false);
      callbackRef.current.onSuccessCallBack();
    }
  }, [isOtpCorrect, otpAuthorizationError?.action]);

  useEffect(() => {
    if (!otpAuthorizationError?.action) {
      setActionKey(null);
      setIsOtpCorrect(false);
    }
  }, [otpAuthorizationError?.action]);

  return {
    isLoadingAuthAction,
    closeOtpAuthorizationPrompt,
    handleOrderModuleAuth,
    clearLatestOtpAuthCode,
    getAuthCodeBaseOnKey,
  };
};
